/**
 * Reusable field components for Activity Cards
 * Standard icon + text patterns used across all activity types
 */

import { ReactNode } from 'react';
import { MapPin, Calendar, Clock, Euro, Link, LucideIcon, BedDouble, Users, FileText } from 'lucide-react';
import { formatDateRange, formatActivityTime, shouldShowCost, shouldShowLink } from './utils';
import { useI18n } from '@/hooks/useI18n';
import { RateHawkBookingMetadata } from '../models/activity.model';
import { formatMetapolicyEntries, parsePolicyText, formatCurrency } from '../ratehawk/ratehawk-accommodation/helpers';

interface ActivityFieldProps {
  icon: LucideIcon;
  children: ReactNode;
  className?: string;
  truncate?: boolean;
}

/**
 * Base component for icon + text field display
 */
export const ActivityField = ({ icon: Icon, children, className = "", truncate = false }: ActivityFieldProps) => {
  return (
    <div className={`flex items-center gap-2 ${truncate ? 'min-w-0' : ''} ${className}`}>
      <Icon className="h-3 w-3 xl:h-4 xl:w-4 shrink-0" />
      <span className={truncate ? 'truncate min-w-0' : ''}>{children}</span>
    </div>
  );
};

interface ActivityAddressProps {
  address: string;
}

/**
 * Display address field with MapPin icon, capped at 35% of the row width
 */
export const ActivityAddress = ({ address }: ActivityAddressProps) => {
  if (!address) return null;
  return <ActivityField icon={MapPin} className="max-w-[35%]" truncate>{address}</ActivityField>;
};

interface ActivityDateRangeProps {
  start_date: string;
  end_date: string;
}

/**
 * Display date range field with Calendar icon
 */
export const ActivityDateRange = ({ start_date, end_date }: ActivityDateRangeProps) => {
  if (!start_date || !end_date) return null;
  return (
    <ActivityField icon={Calendar}>
      {formatDateRange(start_date, end_date)}
    </ActivityField>
  );
};

interface ActivityTimeRangeProps {
  start_time: string;
  end_time: string;
}

/**
 * Display time range field with Clock icon
 */
export const ActivityTimeRange = ({ start_time, end_time }: ActivityTimeRangeProps) => {
  if (!start_time || !end_time) return null;
  return (
    <ActivityField icon={Clock}>
      {formatActivityTime(start_time)} - {formatActivityTime(end_time)}
    </ActivityField>
  );
};

interface ActivityTimeProps {
  time: string;
}

/**
 * Display single time field with Clock icon
 */
export const ActivityTime = ({ time }: ActivityTimeProps) => {
  if (!time) return null;
  return (
    <ActivityField icon={Clock}>
      {formatActivityTime(time)}
    </ActivityField>
  );
};

interface ActivityCostProps {
  cost: number | null | undefined;
  isAdmin: boolean;
  isTraveler: boolean;
}

/**
 * Display cost field with Euro icon (with permission check)
 */
export const ActivityCost = ({ cost, isAdmin, isTraveler }: ActivityCostProps) => {
  if (!shouldShowCost(cost, isAdmin, isTraveler)) return null;
  return <ActivityField icon={Euro}>{cost}</ActivityField>;
};

interface ActivityLinkProps {
  link: string | null | undefined;
  isAdmin: boolean;
  isTraveler: boolean;
}

/**
 * Display external link as a chip button (with permission check)
 */
export const ActivityLink = ({ link, isAdmin, isTraveler }: ActivityLinkProps) => {
  if (!shouldShowLink(link, isAdmin, isTraveler)) return null;
  
  return (
    <a
      href={link!}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 mt-2 px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
    >
      <Link className="h-3 w-3 flex-shrink-0" />
      <span className="truncate max-w-[200px]">{link}</span>
    </a>
  );
};

interface ActivityRoomsGuestsProps {
  metadata: RateHawkBookingMetadata | Record<string, unknown> | undefined;
}

/**
 * Display rooms/guests summary with BedDouble + Users icons
 */
export const ActivityRoomsGuests = ({ metadata }: ActivityRoomsGuestsProps) => {
  const { t } = useI18n();
  const guestsPerRoom = (metadata as RateHawkBookingMetadata | undefined)?.ratehawk_guests_per_room;

  if (!guestsPerRoom || guestsPerRoom.length === 0) return null;

  const roomsCount = (metadata as RateHawkBookingMetadata).ratehawk_rooms ?? guestsPerRoom.length;
  const guestsCount = guestsPerRoom.reduce(
    (total, room) => total + room.adults + room.childrenAges.length,
    0
  );

  return (
    <div className="flex items-center gap-4">
      <ActivityField icon={BedDouble}>
        {roomsCount}
        <span className="hidden md:inline"> {t('activityCard.roomsLabel', { count: roomsCount })}</span>
      </ActivityField>
      <ActivityField icon={Users}>
        {guestsCount}
        <span className="hidden md:inline"> {t('activityCard.guestsLabel', { count: guestsCount })}</span>
      </ActivityField>
    </div>
  );
};

interface ActivityHotelPoliciesProps {
  metadata: RateHawkBookingMetadata | Record<string, unknown> | undefined;
}

/**
 * Display ETG hotel policies (metapolicy_struct + metapolicy_extra_info),
 * captured at booking time. Reuses the same flattening/parsing helpers as
 * the live RateHawk search (RatesView's HotelPoliciesBox).
 */
export const ActivityHotelPolicies = ({ metadata }: ActivityHotelPoliciesProps) => {
  const { t } = useI18n();
  const struct = (metadata as RateHawkBookingMetadata | undefined)?.hotel_metapolicy_struct;
  const extraInfo = (metadata as RateHawkBookingMetadata | undefined)?.hotel_metapolicy_extra_info;
  const entries = formatMetapolicyEntries(struct);
  const extraInfoLines = extraInfo?.split("\n").filter(Boolean) ?? [];

  if (entries.length === 0 && extraInfoLines.length === 0) return null;

  return (
    <div>
      <span className="font-medium mb-1 flex items-center gap-1">
        <FileText className="h-3 w-3" />
        {t('activityCard.hotelPolicies')}
      </span>
      <div className="text-gray-600 text-sm space-y-0.5">
        {entries.map(({ key, label, text }) => (
          <p key={key}>
            <span className="font-medium text-gray-700">{label}:</span> {text}
          </p>
        ))}
        {extraInfoLines.map((line, i) => (
          <p key={i}>
            {parsePolicyText(line).map((seg, j) =>
              seg.href ? (
                <a
                  key={j}
                  href={seg.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 underline"
                >
                  {seg.text}
                </a>
              ) : (
                <span key={j}>{seg.text}</span>
              )
            )}
          </p>
        ))}
      </div>
    </div>
  );
};

interface ActivityNonIncludedTaxesProps {
  metadata: RateHawkBookingMetadata | Record<string, unknown> | undefined;
}

/**
 * Display the taxes not included in the rate price (e.g. city tax paid at
 * check-in), captured at booking time from ETG's tax_data.
 */
export const ActivityNonIncludedTaxes = ({ metadata }: ActivityNonIncludedTaxesProps) => {
  const { t } = useI18n();
  const taxes = (metadata as RateHawkBookingMetadata | undefined)?.taxes;
  const nonIncludedTaxes = taxes?.filter((tax) => !tax.included_by_supplier) ?? [];

  if (nonIncludedTaxes.length === 0) return null;

  return (
    <div>
      <span className="font-medium mb-1 block text-amber-700">{t('activityCard.nonIncludedTaxes')}</span>
      <div className="text-sm space-y-0.5">
        {nonIncludedTaxes.map((tax, i) => (
          <p key={i} className="text-amber-700">
            {tax.name.replace(/_/g, " ")}: {formatCurrency(tax.amount, tax.currency_code)}
          </p>
        ))}
        <p className="text-xs text-amber-600">{t('activityCard.taxesPayAtCheckin')}</p>
      </div>
    </div>
  );
};
